import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useInviteToken } from '@/hooks/useInviteToken';

interface LeaveChallengeProps {
  onInviteClick?: () => void;
}

export function LeaveChallenge({ onInviteClick }: LeaveChallengeProps) {
  const { user, membership, group, refreshMembership } = useAuth();
  const { t } = useLanguage();
  const { token: inviteToken } = useInviteToken();
  const [isProcessing, setIsProcessing] = useState(false);

  if (!membership || !group) {
    return null;
  }

  const isCreator = membership.role === 'creator';

  const handleLeaveChallenge = async () => {
    if (!user || !membership) return;

    setIsProcessing(true);

    try {
      const { error } = await supabase
        .from('memberships')
        .delete()
        .eq('id', membership.id)
        .eq('user_id', user.id);

      if (error) {
        toast.error(t('leaveChallengeFailed'));
        console.error('Leave challenge error:', error);
      } else {
        toast.success(t('leaveChallengeSuccess'));
        await refreshMembership();
      }
    } catch {
      toast.error(t('authUnexpectedError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelChallenge = async () => {
    if (!user || !group) return;

    setIsProcessing(true);

    try {
      const { error } = await supabase
        .rpc('cancel_group_challenge', { p_group_id: group.id });

      if (error) {
        toast.error(t('cancelChallengeFailed'));
        console.error('Cancel challenge error:', error);
      } else {
        toast.success(t('cancelChallengeSuccess'));
        await refreshMembership();
      }
    } catch {
      toast.error(t('authUnexpectedError'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className="border-destructive/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">{t('leaveChallengeTitle')}</CardTitle>
        <CardDescription>
          {isCreator ? t('leaveChallengeCreator') : t('leaveChallengeMember')}{' '}
          <button
            onClick={onInviteClick}
            className="font-mono font-bold underline underline-offset-2 decoration-primary/50 hover:decoration-primary transition-colors"
          >
            {inviteToken || '···'}
          </button>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t('leaveChallengeStarted')} {new Date(group.challenge_start).toLocaleDateString()}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('leaveChallengeEnds')} {new Date(group.challenge_end).toLocaleDateString()}
          </p>

          {isCreator ? (
            /* Creator sees Cancel Challenge */
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isProcessing}>
                  {isProcessing ? t('leaveChallengeCancelling') : t('leaveChallengeCancel')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('leaveDialogTitleCancel')}</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>
                      {t('leaveDialogDescCancel')} <strong>{t('leaveDialogDescCancelBold')}</strong>.
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>{t('leaveDialogCancelPoint1')}</li>
                      <li>{t('leaveDialogCancelPoint2')}</li>
                      <li>{t('leaveDialogCancelPoint3')}</li>
                      <li>{t('leaveDialogCancelPoint4')} <strong>{t('leaveDialogIrreversible')}</strong></li>
                    </ul>
                    <p className="pt-2">
                      {t('leaveDialogConfirmCancel')} <strong>{inviteToken || '···'}</strong>?
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('leaveDialogKeep')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleCancelChallenge}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t('leaveDialogYesCancel')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            /* Regular members see Leave Challenge */
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={isProcessing}>
                  {isProcessing ? t('leaveChallengeLeaving') : t('leaveChallengeLeave')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('leaveDialogTitleLeave')}</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>
                      {t('leaveDialogDescLeave')} <strong>{t('leaveDialogIrreversible')}</strong>. {t('leaveDialogDescLeaveIf')}
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>{t('leaveDialogLeavePoint1')}</li>
                      <li>{t('leaveDialogLeavePoint2')}</li>
                      <li>{t('leaveDialogLeavePoint3')}</li>
                    </ul>
                    <p className="pt-2">
                      {t('leaveDialogConfirmLeave')} <strong>{inviteToken || '···'}</strong>?
                    </p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleLeaveChallenge}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {t('leaveDialogYesLeave')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
